import { Loading, ToastProvider } from '@iheartjane/reefer';
import { Suspense, useEffect } from 'react';
import type { PropsWithChildren } from 'react';
import type { RouteObject } from 'react-router';
import { Navigate, Outlet } from 'react-router';

import { Authenticated } from '@jane/business-admin/components/auth/Authenticated';
import { RequireLoggedInUser } from '@jane/business-admin/components/auth/RequireLoggedInUser';
import { ErrorView } from '@jane/business-admin/components/ErrorView';
import { GlobalSpecialsModal } from '@jane/business-admin/components/globalSpecials/modal/GlobalSpecialsModal';
import { IdentifyManager } from '@jane/business-admin/components/IdentifyManager';
import { NotFoundPage } from '@jane/business-admin/components/NotFoundPage';
import { RumErrorBoundary as ErrorBoundary } from '@jane/business-admin/components/RumErrorBoundary';
import { SquareOAuth } from '@jane/business-admin/components/square/SquareOAuth';
import { UnauthorizedAccess } from '@jane/business-admin/components/UnauthorizedAccess';
import {
  useHasPermissions,
  useIsFulfillmentV2Enabled,
  useOnlyFulfillmentsPermission,
} from '@jane/business-admin/hooks';
import { useAuthenticated } from '@jane/business-admin/hooks/useAuthenticated';
import { Permission } from '@jane/shared/auth';
import { load } from '@jane/shared/util';

import { analyticsRoutes } from './routing/analyticsRoutes';
import { authRoutes } from './routing/authRoutes';
import { premiumRoutes } from './routing/premiumRoutes';
import { storeRoutes } from './routing/storeRoutes';

const BusinessSolutions = load(() => import('./features/BusinessSolutions'));
const TermsOfService = load(() => import('./features/TermsOfService'));
const PrivacyPolicy = load(() => import('./features/PrivacyPolicy'));
const GlobalSpecials = load(() => import('./features/GlobalSpecials'));
const BoostMenus = load(() => import('./features/BoostMenus'));

const Shell = load(() => import('./features/Shell'));

const PublicRoot = () => (
  <Authenticated>
    <IdentifyManager />
    <ToastProvider verticalOffset={96} usePortal>
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    </ToastProvider>
  </Authenticated>
);

// Full-page navigation to the fulfillment Rails UI. <Navigate> would only
// update SPA history and never load the server-rendered page; the existing
// warden session cookie (set at manager login) flows along with the
// browser-level navigation.
// nav_source lets Rails fire the "Navigated" event server-side; we don't track
// here to avoid double-counting the Rails page view. Exported for testing.
export const FulfillmentRedirect = () => {
  useEffect(() => {
    window.location.replace(
      '/web/fulfillment?nav_source=fulfillment_auto_redirect'
    );
  }, []);
  return <Loading />;
};

const GatedRoot = ({ children }: PropsWithChildren) => {
  const isOnlyFulfillments = useOnlyFulfillmentsPermission();
  const isFulfillmentV2Enabled = useIsFulfillmentV2Enabled();

  if (isOnlyFulfillments === undefined) {
    return <Loading />;
  }

  if (isOnlyFulfillments && isFulfillmentV2Enabled) {
    return <FulfillmentRedirect />;
  }

  return (
    <RequireLoggedInUser>
      <Shell>
        <ErrorBoundary fallback={ErrorView} scope="authenticated">
          <Outlet />
          {children}
        </ErrorBoundary>
      </Shell>
    </RequireLoggedInUser>
  );
};

const FallbackRoute = () => {
  const isLoggedIn = useAuthenticated();

  return isLoggedIn ? (
    <GatedRoot>
      <NotFoundPage />
    </GatedRoot>
  ) : (
    <Navigate to="/login" replace />
  );
};

const GatedBoostMenuRoute = () => {
  const isAllowed = useHasPermissions([Permission.EditEmbedConfigs]);

  if (isAllowed === undefined) return null;

  return !isAllowed ? (
    <UnauthorizedAccess resourceName="boost menus" />
  ) : (
    <BoostMenus />
  );
};

export const createRoutes = (): RouteObject[] => [
  {
    element: <PublicRoot />,
    errorElement: <NotFoundPage />,
    children: [
      /** Unauthenticated routes */
      {
        path: '/',
        element: <Navigate to="/login" replace />,
      },
      {
        path: '*',
        element: <FallbackRoute />,
      },
      ...authRoutes(),
      {
        path: '/privacy-policy',
        element: <PrivacyPolicy />,
      },
      {
        path: '/square-oauth',
        element: <SquareOAuth />,
      },
      {
        path: '/terms',
        element: <TermsOfService />,
      },
      /** Authenticated routes */
      {
        element: <GatedRoot />,
        children: [
          ...analyticsRoutes(),
          {
            path: '/boost',
            element: <GatedBoostMenuRoute />,
          },
          {
            path: '/marketing',
            children: [
              {
                path: 'business-solutions',
                element: <BusinessSolutions />,
              },
              {
                path: '/marketing/',
                element: <Navigate to="business-solutions" replace={true} />,
              },
            ],
          },
          ...premiumRoutes(),
          {
            path: '/specials',
            element: <GlobalSpecials />,
            children: [
              {
                path: ':special_id',
                element: <GlobalSpecialsModal />,
              },
              {
                path: 'duplicate/:special_id',
                element: <GlobalSpecialsModal />,
              },
            ],
          },
          ...storeRoutes(),
        ],
      },
    ],
  },
];
